import { useEffect, useState } from "react";
import { getInactiveStores } from "../../services";
import {
  InactiveStoreListQuery,
  InactiveStoreResponse,
} from "../../types/inactive-stores.type";
import { PAGE_SIZE } from "./inactiveStores";

interface InactiveStorePage {
  rows: InactiveStoreResponse[];
  count: number;
  /** 0-based, as `TablePagination` counts. */
  page: number;
  setPage: (page: number) => void;
  loading: boolean;
  hasError: boolean;
}

/**
 * One page of the shop list, for the detail table and the popup alike.
 *
 * `query` must keep its identity until one of its values changes (memoise it): each new object
 * is a new request, and every request reruns a multi-second query on the server. A new query
 * starts again from the first page, and only the latest request may settle the state.
 */
export function useInactiveStorePage(query: InactiveStoreListQuery): InactiveStorePage {
  const [page, setPage] = useState(0);
  const [pagedQuery, setPagedQuery] = useState(query);
  const [rows, setRows] = useState<InactiveStoreResponse[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Reset during render rather than in an effect, so a filter change sends one request for
  // page 1 instead of one for the stale page followed by another once the reset lands.
  if (pagedQuery !== query) {
    setPagedQuery(query);
    setPage(0);
  }

  useEffect(() => {
    let isLatest = true;
    setLoading(true);
    setHasError(false);
    getInactiveStores({ ...query, page: page + 1, limit: PAGE_SIZE })
      .then((response) => {
        if (!isLatest) return;
        setRows(response.data.data?.rows ?? []);
        setCount(response.data.data?.count ?? 0);
      })
      .catch(() => {
        // The axios interceptor has already shown the server's message; this only clears
        // the table so it cannot show rows that belong to another query.
        if (!isLatest) return;
        setHasError(true);
        setRows([]);
        setCount(0);
      })
      .finally(() => {
        if (isLatest) setLoading(false);
      });
    return () => {
      isLatest = false;
    };
  }, [query, page]);

  return { rows, count, page, setPage, loading, hasError };
}
